import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCopy } from './copy/copyMode';
import { WaitlistCTA } from './WaitlistCTA';
import { trackPricingPlanSelected } from '../lib/analytics';
import { SectionIntro } from './alt/SectionIntro';
import { TALK_TO_SALES_URL } from '../lib/links';
export type Tier = {
  credits: number;
  price: number;
  popular?: boolean;
};
export type Group = {
  group: string;
};
export type DropdownItem = Tier | Group;
// Exported so other surfaces (e.g. the dashboard billing modal) present the
// exact same tiers and groupings as the pricing page.
export const PRICING_DATA: DropdownItem[] = [{
  credits: 20000,
  price: 50
}, {
  credits: 30000,
  price: 75
}, {
  credits: 40000,
  price: 100
}, {
  credits: 80000,
  price: 200
}, {
  group: 'Small-sized companies'
}, {
  credits: 125000,
  price: 300
}, {
  credits: 160000,
  price: 400
}, {
  credits: 200000,
  price: 500
}, {
  credits: 300000,
  price: 750,
  popular: true
}, {
  credits: 400000,
  price: 1000
}, {
  credits: 600000,
  price: 1500
}, {
  credits: 800000,
  price: 2000
}, {
  credits: 1200000,
  price: 3000
}, {
  credits: 1600000,
  price: 4000
}, {
  credits: 2000000,
  price: 5000
}, {
  group: 'Medium-sized companies'
}, {
  credits: 3000000,
  price: 7500
}, {
  credits: 4000000,
  price: 10000
}, {
  credits: 5000000,
  price: 12500
}, {
  credits: 6000000,
  price: 15000
}, {
  credits: 8000000,
  price: 20000
}, {
  credits: 10000000,
  price: 25000
}, {
  credits: 12000000,
  price: 30000
}, {
  group: 'Enterprise'
}, {
  credits: 14000000,
  price: 35000
}, {
  credits: 16000000,
  price: 40000
}, {
  credits: 18000000,
  price: 45000
}, {
  credits: 20000000,
  price: 50000
}];
function FeatureItem({
  text,
  dark = false
}: {
  text: string;
  dark?: boolean;
}) {
  // A bare orange tick rather than a filled disc. Five filled circles stacked
  // down a card is five heavy marks competing with the price, which is the one
  // thing the card exists to state.
  return <li className={`flex items-start gap-3 text-[14.5px] leading-relaxed md:text-[15.5px] ${dark ? 'text-white/80' : 'text-[#6B5D52]'}`} data-id="element-4837" data-mp="merzb9iu62es">
      <Check size={16} strokeWidth={2.6} className={`mt-[3px] shrink-0 ${dark ? 'text-[#FB923C]' : 'text-[#EA580C]'}`} data-id="element-4838" data-mp="ci5fmo28xhec" />
      <span data-id="element-4839" data-mp="3uvms1w5x66f5">{text}</span>
    </li>;
}
export function PricingSection() {
  const copy = useCopy();
  const [selectedTier, setSelectedTier] = useState<Tier>(PRICING_DATA[0] as Tier);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsDropdownOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  return <section className="relative w-full bg-[#FAF8F4] py-16 md:py-32 overflow-hidden" data-id="element-4840" data-mp="31b558fzvq0k0">
      {/* Halftone dot-matrix texture */}
      <div aria-hidden="true" className="absolute inset-0 dot-matrix opacity-40 pointer-events-none z-0" data-id="element-4841" data-mp="3g7mft508vb5i" />
      

      <div className="max-w-[1060px] mx-auto px-6 md:px-10 relative z-10" data-id="element-4842" data-mp="3p29gjnuyqzbf">
        {/* Header. Left-aligned, like every section intro on the home page —
         a centred headline over a left-aligned two-card grid gave the section
         two axes. The lead line states the offer once, up front, so the reader
         knows the price is only relevant later. */}
        <SectionIntro align="left" className="mb-12 max-w-3xl md:mb-16" data-id="element-4843" data-mp="3u36zw3rplzei">
          <h2 className="font-title text-[40px] font-bold leading-[1.04] tracking-[-0.03em] text-[#2B211A] sm:text-[52px] md:text-[60px]" data-id="element-4844" data-mp="hx178kds6wds">
            {copy.pricingSection.headline}
          </h2>
          <p className="mt-6 max-w-[56ch] text-[16.5px] leading-[1.7] text-[#6B5D52] md:text-[17.5px]" data-id="element-4845" data-mp="qrfo2rdymez0">
            Start with $100 in credits, free. Credits are shared across your
            whole workspace, and you only pick a plan once they run out.
          </p>
        </SectionIntro>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-4846" data-mp="301xoqzdctgo">
          {/* Team Card */}
          <div className="relative z-20 flex flex-col rounded-[22px] border border-[#E7E0D4] bg-white p-7 shadow-[0_1px_2px_rgba(43,33,26,0.04),0_24px_60px_-40px_rgba(43,33,26,0.35)] md:p-9" data-id="element-4847" data-mp="7yva0g2x6ef9">
            <div className="mb-8" data-id="element-4848" data-mp="1jfuyf0m5c940">
              <span className="mb-5 block text-[12px] font-bold uppercase tracking-[0.16em] text-[#EA580C]" data-id="element-4849" data-mp="1jetpb1ooxwox">
                Team
              </span>
              <div className="mb-6 flex items-baseline gap-1.5" data-id="element-4850" data-mp="38wqikuupwmv7">
                <span className="font-title text-[52px] font-bold leading-none tracking-[-0.03em] text-[#2B211A] md:text-[60px]" data-id="element-4851" data-mp="38y1nwz3ta2of">
                  ${selectedTier.price.toLocaleString()}
                </span>
                <span className="text-[15px] font-medium text-[#A2917F]" data-id="element-4852" data-mp="2u7awhs64t5lu">
                  / month
                </span>
              </div>

              <div className="relative space-y-2" ref={dropdownRef} data-id="element-4853" data-mp="2q6qx0hy35pwj">
                <label className="block text-[13px] font-semibold text-[#A2917F]" data-id="element-4854" data-mp="2l6w7ahq1r6d2">
                  Shared workspace credits
                </label>
                <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} aria-haspopup="listbox" aria-expanded={isDropdownOpen} aria-label="Select monthly credit tier" className="flex w-full items-center justify-between rounded-2xl border border-[#E7E0D4] bg-[#FAF8F4] px-5 py-4 text-left transition-colors duration-200 ease-out hover:border-[#D9CFBE] hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EA580C]/35" data-id="element-4855" data-mp="2g81ovgvzrjth">
                  <span className="text-[16.5px] font-semibold text-[#2B211A]" data-id="element-4856" data-mp="28nqv2skkxma1">
                    {selectedTier.credits.toLocaleString()} credits monthly
                  </span>
                  <ChevronDown size={19} className={`text-[#A2917F] transition-transform duration-200 ease-out ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-4857" data-mp="1tx9utcjol2eq" />
                  
                </button>

                <AnimatePresence data-id="element-4858" data-mp="2mlyynjc4flu6">
                  {isDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.15
                }} role="listbox" aria-label="Monthly credit tiers" className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[360px] overflow-y-auto rounded-2xl border border-[#E7E0D4] bg-white p-2 shadow-[0_1px_2px_rgba(43,33,26,0.06),0_28px_70px_-30px_rgba(43,33,26,0.45)]" data-id="element-4859" data-mp="2rltm1cwottah">
                    
                      {PRICING_DATA.map((item, idx) => {
                    if ('group' in item) {
                      return <div key={`group-${idx}`} className="px-4 pb-2 pt-6 text-[11px] font-bold uppercase tracking-[0.14em] text-[#A2917F]" data-id="element-4860" data-mp="18m450civci94">
                            
                              {item.group}
                            </div>;
                    }
                    const isSelected = item.credits === selectedTier.credits;
                    return <button key={item.credits} role="option" aria-selected={isSelected} onClick={() => {
                      trackPricingPlanSelected(item.credits, item.price);
                      setSelectedTier(item);
                      setIsDropdownOpen(false);
                    }} className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-left transition-colors duration-150 ease-out ${isSelected ? 'bg-[#FFF3EA] text-[#EA580C]' : 'text-[#2B211A] hover:bg-[#FAF8F4]'}`} data-id="element-4861" data-mp="pqe3srirfpwa" data-mpi={idx}>
                          
                            <div className="flex items-center gap-3" data-id="element-4862" data-mp="37x77hu7jur7d">
                              <div className="w-5 flex justify-center" data-id="element-4863" data-mp="3ctegjvv8sitj">
                                {isSelected && <Check size={16} strokeWidth={3} data-id="element-4864" data-mp="3gwu8iewd9uzw" />}
                              </div>
                              <span className="font-medium" data-id="element-4865" data-mp="3ltuiv2091jwt">
                                {item.credits.toLocaleString()} credits monthly
                              </span>
                            </div>
                            <div className="flex items-center gap-3" data-id="element-4866" data-mp="3vm09a7aurt9w">
                              {item.popular && <span className="rounded-full bg-[#FFF3EA] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-[#EA580C]" data-id="element-4867" data-mp="4dcnjq8pa41t">
                                  Most popular
                                </span>}
                              <span className={`font-semibold tabular-nums ${isSelected ? 'text-[#EA580C]' : 'text-[#A2917F]'}`} data-id="element-4868" data-mp="dczk9yqhiloj">
                              
                                ${item.price.toLocaleString()}
                              </span>
                            </div>
                          </button>;
                  })}
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>

            <WaitlistCTA analyticsLabel="pricing-team" className="mb-4 flex w-full justify-center rounded-full bg-[#EA580C] py-3.5 text-[15px] font-bold text-white transition-colors duration-200 ease-out hover:bg-[#C2410C]" data-id="element-4869" data-mp="2gnukmbibgse7">
              Get Started for Free
            </WaitlistCTA>
            {/* The offer, restated where the decision is actually made. */}
            <p className="mb-8 text-center text-[13px] text-[#A2917F]" data-id="element-4870" data-mp="30blhbr50uo6u">
              $100 in credits free · No card required
            </p>

            {/* Hairline above the list rather than a gap: it separates the
             price decision from what you get without adding another box. */}
            <ul className="flex-1 space-y-3.5 border-t border-[#EDE6DA] pt-7" data-id="element-4871" data-mp="14x4wykfr56kl">
              <FeatureItem text="Slack-native agent in threads + mentions" data-id="element-4872" data-mp="zybkmvc7blm0" />
              <FeatureItem text="Persistent workspace context" data-id="element-4873" data-mp="1dwrtartmjm6s" />
              <FeatureItem text="Integrations + tool execution" data-id="element-4874" data-mp="19tvd17rk4260" />
              <FeatureItem text="Scheduled tasks & crons (reports, audits, proactive check-ins)" data-id="element-4875" data-mp="19wrcj0j1yywk" />
              <FeatureItem text="Drafts + artifacts (updates, tickets/docs where supported)" data-id="element-4876" data-mp="1nrtfbesls855" />
            </ul>
          </div>

          {/* Enterprise Card */}
          <div className="relative z-10 flex flex-col overflow-hidden rounded-[22px] bg-[#1A120C] p-7 shadow-[0_1px_2px_rgba(43,33,26,0.1),0_30px_70px_-40px_rgba(43,33,26,0.8)] md:p-9" data-id="element-4877" data-mp="1xio34nlhzfsp">
            {/* Same halftone the ink panels on the home page use, so the two
             dark surfaces on the site read as one material. */}
            <div className="dot-matrix-dark pointer-events-none absolute inset-0 opacity-40" aria-hidden="true" data-id="element-4878" data-mp="21jhrfi2uqwm4" />

            <div className="relative z-10 mb-8" data-id="element-4879" data-mp="2q6fob12u119h">
              <span className="mb-5 block text-[12px] font-bold uppercase tracking-[0.16em] text-[#FB923C]" data-id="element-4880" data-mp="2q54lb3h7nxjf">
                Enterprise
              </span>
              <div className="mb-6 flex items-baseline gap-1" data-id="element-4881" data-mp="2g89qauk5cf9w">
                <span className="font-title text-[52px] font-bold leading-none tracking-[-0.03em] text-white md:text-[60px]" data-id="element-4882" data-mp="2g6ggj6e8lexo">
                  Custom
                </span>
              </div>

              <div className="space-y-2" data-id="element-4883" data-mp="27966fod5nvuc">
                <label className="block text-[13px] font-semibold text-white/50" data-id="element-4884" data-mp="1siyttp9tx71k">
                  Flexible pricing
                </label>
                {/* The same calendar as every other sales control on the site.
                 It previously went to /contact, so the Enterprise tier was the
                 one plan you could not actually book time about. */}
                <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="flex w-full justify-center rounded-full bg-white py-3.5 text-[15px] font-bold text-[#2B211A] transition-[background-color,transform] duration-200 ease-out hover:-translate-y-px hover:bg-[#F5EFE7]" data-id="element-4885" data-mp="1oj6gz0dekeqe">
                  Contact sales
                </a>

              </div>
            </div>

            <div className="relative z-10 mt-2 flex-1 border-t border-white/10 pt-7" data-id="element-4886" data-mp="3cgph1udmie9g">
              <p className="mb-5 text-[13px] font-semibold text-white/50" data-id="element-4887" data-mp="cyg0ml5f4t1f">
                Everything in Team, plus:
              </p>
              <ul className="space-y-3.5" data-id="element-4888" data-mp="7ytramspnwxw">
                <FeatureItem text="Invoicing + custom billing terms" dark data-id="element-4889" data-mp="3y027q3j8c3f" />
                <FeatureItem text="Security review support + DPA" dark data-id="element-4890" data-mp="3v5m9rttdgypk" />
                <FeatureItem text="SLA + priority support" dark data-id="element-4891" data-mp="3v7ozsduiyrua" />
                <FeatureItem text="Dedicated onboarding + tailored limits/controls" dark data-id="element-4892" data-mp="2oq1y8e266484" />
                
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}